import { cn } from '@workspace/ui/lib/utils';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import type { CloudflareResizeOptions } from '~/components/CloudflareResponsiveImage/CloudflareResizeOptions';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import Icon from '~/components/Icon/Icon';
import { useGlobal } from '~/hooks/useGlobal';

import styles from './ProductImage.module.scss';
import type { ProductImageProps } from './ProductImage.types';
import { useDynamicFontSize } from './useDynamicFontSize';

/**
 * Shared component for product image display with favorite button and badges overlay
 * Memoized to prevent unnecessary re-renders
 */
const ProductImage = memo(
  ({
    src,
    alt,
    size,
    badges,
    isFavorite,
    onToggleFavorite,
    productSlug,
    offerSku,
    onProductClick,
    className,
    cartItemsCount = 0,
    currentPrice,
    totalCartPrice,
    isOutOfStock = false,
    transparent = false,
  }: ProductImageProps) => {
    const { t } = useTranslation();
    const { isMobile } = useGlobal();

    const showCartOverlay =
      cartItemsCount > 0 && (size === 'm' || size === 's');

    const showOutOfStockOverlay =
      isOutOfStock && (size === 'm' || size === 's');

    const priceFontSize = useDynamicFontSize(totalCartPrice || '', isMobile);
    const itemPriceFontSize = useDynamicFontSize(currentPrice || '', isMobile, {
      mobile: new Map([
        [1, 14],
        [6, 12],
      ]),
      desktop: new Map([
        [1, 20],
        [6, 18],
      ]),
    });

    const handleFavoriteClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onToggleFavorite();
      },
      [onToggleFavorite],
    );

    const badgesContainer = useMemo(() => {
      if (!badges) {
        return null;
      }
      return <div className={styles.badgesContainer}>{badges}</div>;
    }, [badges]);

    const outOfStockOverlay = useMemo(() => {
      if (!showOutOfStockOverlay) {
        return null;
      }

      return (
        <div
          className={cn(
            styles.outOfStockOverlay,
            styles[`outOfStockOverlay_${size}`],
          )}
        >
          <div
            className={cn(
              styles.comingSoonLabel,
              styles[`comingSoonLabel_${size}`],
              styles[`comingSoonLabel_${size}_${isMobile ? 'mobile' : 'web'}`],
            )}
          >
            {t('comingSoon')}
          </div>
        </div>
      );
    }, [showOutOfStockOverlay, size, isMobile, t]);

    const cartOverlayPrice = useMemo(() => {
      if (size !== 'm' || !totalCartPrice) return null;

      if (company.is('mcosmetic')) return null;

      return (
        <div className={styles.cartOverlayPrice}>
          <div
            className={styles.price}
            style={{ fontSize: `${priceFontSize}px` }}
          >
            {totalCartPrice}
          </div>
          <div
            className={styles.itemPrice}
            style={{ fontSize: `${itemPriceFontSize}px` }}
          >
            {currentPrice} / {t('item')}
          </div>
        </div>
      );
    }, [size, totalCartPrice, priceFontSize]);

    const cartOverlay = useMemo(() => {
      if (!showCartOverlay) {
        return null;
      }

      return (
        <div
          className={cn(
            styles.cartOverlay,
            transparent && styles.cartOverlayTransparent,
            styles[`cartOverlay_${size}`],
          )}
        >
          {cartOverlayPrice}
        </div>
      );
    }, [showCartOverlay, size, cartOverlayPrice]);

    const favoriteButton = useMemo(() => {
      if (
        (company.name === 'mcosmetic' && size !== 'm' && size !== 's') ||
        (company.name !== 'mcosmetic' && size !== 'm')
      ) {
        return null;
      }

      return (
        <button
          type="button"
          className={cn(
            styles.favoriteButton,
            isFavorite && styles.favoriteActive,
            company.match({
              mcosmetic:
                size === 's' && 'top-1! right-[6px]! md:top-3! md:right-3!',
            }),
          )}
          onClick={handleFavoriteClick}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          data-button="favourite"
        >
          <Icon
            type={isFavorite ? 'favs-card-fill-3.1' : 'favs-card-3.1'}
            size={isMobile ? 20 : 24}
          />
        </button>
      );
    }, [size, handleFavoriteClick, isFavorite, isMobile]);

    const imageSize = useMemo<CloudflareResizeOptions>(() => {
      switch (size) {
        case 's': {
          return isMobile
            ? { width: 224, height: 179 }
            : { width: 264, height: 164 };
        }
        case 'xs': {
          return isMobile
            ? { width: 86, height: 64 }
            : { width: 242, height: 128 };
        }
        case 'm': {
          return isMobile
            ? { width: 128, height: 96 }
            : { width: 320, height: 240 };
        }
        case 'xl': {
          return isMobile
            ? { width: 176, height: 176 }
            : { width: 338, height: 338 };
        }
        case 'l': {
          return isMobile
            ? { width: 176, height: 176 }
            : { width: 260, height: 260 };
        }
        default: {
          return { width: 320, height: 256 };
        }
      }
    }, [size, isMobile]);

    return (
      <Link
        to={`/p/${productSlug}`}
        state={{ selectedOffer: offerSku }}
        onClick={onProductClick}
        className={cn(styles.imageContainer, styles[`size_${size}`], className)}
        data-company={company.name}
        data-link={`product-${productSlug}`}
      >
        <CloudflareResponseImage
          src={src}
          alt={alt}
          className={cn(styles.image, transparent && 'z-1!')}
          srcParams={imageSize}
        />

        <div
          className={cn(
            styles.gradientOverlay,
            styles[`gradientOverlay_${size}`],
            transparent &&
              'z-0! rounded-[inherit] bg-graphic-transparent-white-25! backdrop-blur-[10px]',
          )}
        />

        {badgesContainer}

        {outOfStockOverlay}

        {cartOverlay}

        {favoriteButton}
      </Link>
    );
  },
);

ProductImage.displayName = 'ProductImage';

export default ProductImage;
